import { useEffect, useRef } from 'react'
import { TabContent, TabPane } from 'reactstrap'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import ChatSection from './chat-section'
import ScreenNavigation from './ScreenNavigation'
import { setScreen } from '../../../redux/reducers/messenger/screenSlice'
import { closeLeftSide } from '../../../redux/reducers/messenger/mainSidebarSlice'
import { hideOtherFeature } from '../../../constants'
import RecentSection from './recent-section'

const LeftSidebarChat = () => {
  const dispatch = useAppDispatch()
  const { isChatList } = useAppSelector((state) => state.templateCustomizer)
  const { mainSidebarActiveTab } = useAppSelector((state) => state.mainSidebar)
  const screen = useAppSelector((state) => state.screen.screen)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const handleChatSectionClick = () => {
    if (screen !== 'chat') {
      dispatch(setScreen('chat'))
      dispatch(closeLeftSide())
    }
  }

  const showChatSection = screen === 'chat' && !mainSidebarActiveTab

  // Trap wheel scrolling inside the chat panel so it scrolls independently of the
  // host page. Two leaks are closed: (1) scrolling over the fixed header/tabs, which
  // have no scroll container of their own, must not scroll the host page; (2) scrolling
  // a list past its top/bottom must not chain to the host page. React's synthetic
  // onWheel is passive and can't preventDefault, so we attach a native non-passive
  // listener on the panel root.
  useEffect(() => {
    const root = sidebarRef.current
    if (!root) return

    const isScrollable = (el: HTMLElement) => {
      const overflowY = getComputedStyle(el).overflowY
      return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight
    }

    const handleWheel = (e: WheelEvent) => {
      let el = e.target as HTMLElement | null
      while (el) {
        if (isScrollable(el)) {
          const atTop = el.scrollTop <= 0 && e.deltaY < 0
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0
          // Let the list scroll natively; only block chaining at its boundaries.
          if (atTop || atBottom) e.preventDefault()
          return
        }
        if (el === root) break
        el = el.parentElement
      }
      // Cursor is over a non-scrollable region (header/tabs) → block host-page scroll.
      e.preventDefault()
    }

    root.addEventListener('wheel', handleWheel, { passive: false })
    return () => root.removeEventListener('wheel', handleWheel)
  }, [])

  return (
    <div ref={sidebarRef} className={`meetzy-left-sidebar left-disp`}>
      {showChatSection && (
        <div className={`recent-default dynamic-sidebar cursor-pointer active`} onClick={handleChatSectionClick}>
          {isChatList && <RecentSection />}
          <ChatSection />
        </div>
      )}
      <TabContent activeTab={mainSidebarActiveTab}>
        <TabPane tabId="status" className="status-tab custom-scroll dynamic-sidebar">
          <ScreenNavigation />
        </TabPane>
        <TabPane tabId="call" className="status-tab custom-scroll dynamic-sidebar">
          <ScreenNavigation />
        </TabPane>
        <TabPane tabId="archive" className="contact-list-tab dynamic-sidebar custom-scroll">
          <ScreenNavigation />
        </TabPane>
        {!hideOtherFeature.hideNotification && (
          <TabPane tabId="notification" className="notification-tab dynamic-sidebar custom-scroll">
            <ScreenNavigation />
          </TabPane>
        )}

        <TabPane tabId="document" className="document-tab custom-scroll dynamic-sidebar">
          <ScreenNavigation />
        </TabPane>
        {!hideOtherFeature.hideFriendSuggestions && (
          <TabPane tabId="friend-suggestions" className="contact-list-tab dynamic-sidebar custom-scroll">
            <ScreenNavigation />
          </TabPane>
        )}
        <TabPane tabId="blockicon" className="contact-list-tab dynamic-sidebar custom-scroll">
          <ScreenNavigation />
        </TabPane>
        <TabPane tabId="favorite" className="favorite-tab custom-scroll dynamic-sidebar">
          <ScreenNavigation />
        </TabPane>
        <TabPane tabId="settings" className="settings-tab dynamic-sidebar custom-scroll">
          <ScreenNavigation />
        </TabPane>
      </TabContent>
    </div>
  )
}

export default LeftSidebarChat
